import Vue from 'vue';
import { range, capitalize } from 'lodash';
import siteService from '@/project/site/site.service';
import dictionaryService from '@/core/translation/dictionary.service';

import 'vue-airbnb-style-datepicker/dist/vue-airbnb-style-datepicker.min.css';
import Deferred from '@/core/async/Deferred';
import serverContextService from '@/core/serverContext.service';
import tailwind from '@/styling/tailwind.config';

// A random monday 20/4-2020 (remember month is 0-based)
const days = range(20, 27)
    .map(day => new Date(Date.UTC(2020, 3, day)))
    .map(date => toLocaleString(date, { weekday: 'long' }));

const daysShort = range(20, 27)
    .map(day => new Date(Date.UTC(2020, 3, day)))
    .map(date => toLocaleString(date, { weekday: 'short' }));

// Month is 0-based
const monthNames = range(0, 12)
    .map(month => new Date(Date.UTC(2020, month, 1)))
    .map(date => toLocaleString(date, { month: 'long' }));

function toLocaleString(date: Date, options: Intl.DateTimeFormatOptions): string {
    // Will fallback to browsers locale if we don't have culture
    const culture = siteService.getCulture() || '';
    try {
        return capitalize(date.toLocaleString(culture, options));
    } catch {
        // Edge has been to blow up here sometimes.
        return capitalize(date.toLocaleString('en', options));
    }
}

// see docs for available options
const selectedColor = serverContextService?.isOprema ? tailwind.theme.colors.opremablue['300'] : tailwind.theme.colors.blue['500'];
const datepickerOptions = {
    days,
    daysShort,
    monthNames,
    colors: {
        selected: selectedColor,
        inRange: '#C5D2E0',
        selectedText: '#fff',
        text: '#262422',
        inRangeBorder: '#E5EAEF',
        disabled: '#fff',
        hoveredInRange: '#E5EAEF'
    },
    texts: {
        apply: dictionaryService.get('Generic.DatePicker.Apply'),
        cancel: dictionaryService.get('Generic.DatePicker.Cancel')
    }
};

// Load and register datepicker dynamically when needed
const airbnbStyleDatePickerDeferred = new Deferred<void>();

export async function init(): Promise<void> {
    if (!airbnbStyleDatePickerDeferred.isSettled) {
        const airbnbStyleDatepickerDynamicImport = () => import(/* webpackPrefetch: true, webpackChunkName: 'airbnbStyleDatepicker' */ 'vue-airbnb-style-datepicker');
        const { default: airbnbStyleDatepicker } = await airbnbStyleDatepickerDynamicImport();

        Vue.use(airbnbStyleDatepicker, datepickerOptions);
        airbnbStyleDatePickerDeferred.resolve();
    }

    return airbnbStyleDatePickerDeferred.promise;
}

// Keep track of the active date picker's id
export const activeDatePicker: { id: string | null } = Vue.observable({ id: null });
